import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import { RootStore } from 'store/reducer';
import {
  archiveAccount,
  unarchiveAccount,
  selectAccountById,
  removeAccountFromUserGroup,
  inviteAccount
} from 'store/accounts';
import { displayAlert } from 'store/v1/ui/ui.actions';
import Account from 'lib/auth/Account';
import useModal from 'hooks/useModal';

import { Button } from 'shared/buttons';
import { Flyover } from 'v1/components/shared';
import { ConfirmModal, UserModal } from 'modals';

export type UserOptionsProps = {
  id: string;
  userGroupId?: string;
};

const copy = {
  edit: 'Edit user',
  archive: 'Archive user',
  archiveConfirmTitle: 'Are you sure you want to archive this user?',
  archiveConfirmDesc:
    'This will remove the user from all groups they have been added to and they will no longer be able to log into the system.',
  unarchive: 'Unarchive user',
  unarchiveConfirmTitle: 'Are you sure you want to unarchive this user?',
  reinvite: 'Resend invite',
  delete: 'Delete user',
  removeFromGroup: 'Remove user from group',
  groupActions: 'Group Actions',
  userActions: 'User Actions'
};

const UserOptions = ({ id, userGroupId }: UserOptionsProps) => {
  const dispatch = useDispatch();
  const { openModal } = useModal();
  const account = useSelector((state: RootStore) =>
    selectAccountById(state, id)
  );

  const handleEditUser = () => openModal(() => <UserModal id={id} />);

  const handleArchiveUser = () => {
    openModal(() => (
      <ConfirmModal
        title={copy.archiveConfirmTitle}
        description={copy.archiveConfirmDesc}
        confirmLabel={copy.archive}
        onConfirm={() => dispatch(archiveAccount(id))}
      />
    ));
  };

  const handleUnarchiveUser = () => {
    openModal(() => (
      <ConfirmModal
        title={copy.unarchiveConfirmTitle}
        confirmLabel={copy.unarchive}
        onConfirm={() => dispatch(unarchiveAccount(id))}
      />
    ));
  };

  const handleResendInvite = () => {
    (
      dispatch(
        inviteAccount({
          email: account.email,
          isSuperUser: account.isAdmin,
          userGroupIds: account.userGroupIds,
          allowReinviteExistingInvited: true
        })
      ) as unknown as Promise<any>
    )
      .then(unwrapResult)
      .then(() => {
        dispatch(displayAlert('success', 'Invitation was successfully sent'));
      })
      .catch(() => {
        dispatch(displayAlert('error', 'An error occurred'));
      });
  };

  const handleRemoveFromGroup = () => {
    userGroupId &&
      dispatch(removeAccountFromUserGroup({ accountId: id, userGroupId }));
  };

  const renderDefaultUserOptions = () => (
    <>
      <Flyover.Item
        icon="/images/icon_pencil.svg"
        title={copy.edit}
        onClick={handleEditUser}
      />
      <Flyover.Item
        icon="/images/icon_delete.svg"
        title={copy.archive}
        onClick={handleArchiveUser}
      />
    </>
  );

  const renderInvitedUserOptions = () => (
    <>
      <Flyover.Item
        icon="/images/icon_send.svg"
        title={copy.reinvite}
        onClick={handleResendInvite}
      />
      <Flyover.Item
        icon="/images/icon_pencil.svg"
        title={copy.edit}
        onClick={handleEditUser}
      />
      <Flyover.Item
        icon="/images/icon_delete.svg"
        title={copy.delete}
        onClick={handleArchiveUser}
      />
    </>
  );

  const renderArchivedUserOptions = () => (
    <Flyover.Item
      icon="/images/icon_unarchive.svg"
      title={copy.unarchive}
      onClick={handleUnarchiveUser}
    />
  );

  if (!Account.isSuperUser()) {
    return null;
  }

  return (
    <Flyover>
      <Flyover.Trigger>
        <Button icon="options" appearance="silent" size="s" />
      </Flyover.Trigger>
      <Flyover.Content width="S">
        {!!userGroupId && (
          <Flyover.Group title={copy.groupActions}>
            <Flyover.Item
              icon="/images/icon_delete.svg"
              title={copy.removeFromGroup}
              onClick={handleRemoveFromGroup}
            />
          </Flyover.Group>
        )}
        <Flyover.Group title={copy.userActions}>
          {account.dateArchived
            ? renderArchivedUserOptions()
            : account.dateLastLogin
              ? renderDefaultUserOptions()
              : renderInvitedUserOptions()}
        </Flyover.Group>
      </Flyover.Content>
    </Flyover>
  );
};

export default UserOptions;
